import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useKeyHolderNames, useKeyRegister } from '../../hooks/keyRegister';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { sortKeysBySerial } from '../../Helpers/keyRegisterUtils';
import { KeyType } from '../../types/keyRegister';
import KeyTable from './KeyTable';
import KeyTypeModal, { KeyTypeSubmission, toBuildingOptions } from './KeyTypeModal';
import { KeyObjectRef, objectKeyTypes } from './keyObjects';
import { keyCountLabel, keyRegisterLink } from './keyRegisterLabels';
import './KeyRegister.css';

interface ObjectKeysModalProps {
    show: boolean;
    onHide: () => void;
    object: KeyObjectRef;
    /** "Lägenhet 1001", "Förråd 12". */
    objectName: string;
}

/** The link to the register filtered on this object. */
export const objectKeysLink = (object: KeyObjectRef): string =>
    `/keys?in=${encodeURIComponent(`${object.buildingKey}.${object.key}`)}`;

/**
 * Nycklar of a lägenhet, lokal or förråd (admins): the key types linked to it with their keys,
 * then, muted, the types that open the whole building. Read-only: a key row opens it in Nycklar,
 * where events are recorded. "Ny nyckeltyp" creates a type linked to the object. The register is
 * read when the modal opens.
 */
const ObjectKeysModal: React.FC<ObjectKeysModalProps> = ({ show, onHide, object, objectName }) => {
    const navigate = useNavigate();
    const register = useKeyRegister({ enabled: show });
    const { types, keys, keysByType, today, loading, error, submitting } = register;
    const { holderName } = useKeyHolderNames(keys);
    const { buildings } = useBuildingsData();
    const buildingOptions = useMemo(() => toBuildingOptions(buildings), [buildings]);
    const [creating, setCreating] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const { direct, building } = useMemo(() => objectKeyTypes(types, object), [types, object]);

    const createType = async ({ input, filesToUpload }: KeyTypeSubmission) => {
        const created = await register.createType(input);
        setCreating(false);
        if (filesToUpload.length === 0) return;
        try {
            await register.saveTypeAttachments(created.key, filesToUpload, []);
        } catch (err) {
            setSaveError(`Nyckeltypen sparades, men inte alla filer: ${err instanceof Error ? err.message : 'okänt fel'}`);
        }
    };

    const renderType = (type: KeyType, muted: boolean) => {
        const typeKeys = sortKeysBySerial((keysByType[type.key] ?? []).filter(key => key.status !== 'scrapped'));
        return (
            <section key={type.key} className={`object-keys-type${muted ? ' object-keys-type--building' : ''}`} data-testid={`object-keys-type-${type.key}`}>
                <div className="object-keys-type-head">
                    <span className="object-keys-type-name">{type.name}</span>
                    <span className="object-keys-type-count">{keyCountLabel(typeKeys.length)}</span>
                </div>
                {type.opens && <div className="object-keys-type-opens">Öppnar: {type.opens}</div>}
                {typeKeys.length > 0 && (
                    <KeyTable keys={typeKeys} today={today} holderName={holderName} onSelectKey={keyKey => navigate(keyRegisterLink(keyKey))} />
                )}
            </section>
        );
    };

    const renderBody = () => {
        if (loading && types.length === 0) {
            return <div className="text-muted"><Spinner animation="border" size="sm" className="me-2" />Hämtar nycklar…</div>;
        }
        if (error) return <Alert variant="danger">{error}</Alert>;
        return (
            <>
                {direct.length === 0 ? (
                    <p className="text-muted mb-3">Inga nyckeltyper är kopplade till {objectName.toLocaleLowerCase('sv')} ännu.</p>
                ) : (
                    direct.map(type => renderType(type, false))
                )}
                {building.length > 0 && (
                    <>
                        <h6 className="object-keys-building-title">Gäller hela byggnaden</h6>
                        {building.map(type => renderType(type, true))}
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <Modal show={show && !creating} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Nycklar – {objectName}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="key-register-page object-keys">
                    {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>{saveError}</Alert>}
                    {renderBody()}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Link to={objectKeysLink(object)}>Öppna i Nycklar</Link>
                    <Button variant="outline-primary" onClick={() => setCreating(true)} disabled={loading || submitting}>
                        <Plus size={16} className="me-1" aria-hidden="true" />Ny nyckeltyp
                    </Button>
                </Modal.Footer>
            </Modal>
            <KeyTypeModal
                show={show && creating}
                onHide={() => setCreating(false)}
                onSubmit={createType}
                buildings={buildingOptions}
                prefillObject={object}
                submitting={submitting}
            />
        </>
    );
};

export default ObjectKeysModal;
